'use client'

import { useEffect } from 'react'

import { RefreshCw } from 'lucide-react'

import { KineticPageShell } from '../components/kinetic/KineticPageShell'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error(error)
  }, [error])

  const message = error.message || 'An unexpected error occurred.'
  const digestLine = error.digest ? `DIGEST: ${error.digest}` : null

  return (
    <KineticPageShell className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-0 opacity-20">
        <div className="kinetic-error-glitch-bg absolute inset-0" />
        <div className="from-kinetic-error-container/20 absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l to-transparent" />
      </div>

      <main className="relative z-10 flex min-h-screen flex-col items-center overflow-hidden px-4 pb-20 pt-24 md:pt-28">
        <div className="w-full max-w-4xl px-2 sm:px-6">
          <div className="border-kinetic-error/20 mb-10 flex flex-col items-start justify-between gap-4 border-b pb-4 md:flex-row md:items-baseline">
            <div>
              <span className="mb-2 block font-mono text-xs uppercase tracking-widest text-kinetic-error">
                PRIORITY_ALPHA_OVERRIDE
              </span>
              <h1 className="font-headline text-4xl font-black leading-none tracking-tighter text-kinetic-error sm:text-6xl md:text-7xl lg:text-8xl">
                UNEXPECTED_EXCEPTION
              </h1>
            </div>
            <div className="bg-kinetic-error px-3 py-1 font-mono text-2xl font-bold text-kinetic-on-error">
              ERR_500
            </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-12">
            <div className="md:col-span-8">
              <p className="max-w-2xl font-headline text-xl font-light leading-snug text-kinetic-on-surface md:text-3xl">
                A runtime error occurred in the core processing unit. Our engineers have been
                alerted.
              </p>
              <div className="mt-10">
                <button
                  type="button"
                  onClick={() => reset()}
                  className="group bg-kinetic-error px-10 py-4 font-headline text-lg font-bold uppercase tracking-widest text-kinetic-on-error transition-all duration-150 hover:bg-kinetic-error-container hover:shadow-[0_0_15px_rgba(255,180,171,0.4)]"
                >
                  REBOOT_SESSION
                  <RefreshCw
                    className="ml-2 inline-block h-5 w-5 align-middle transition-transform group-hover:rotate-180"
                    aria-hidden
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 md:col-span-4">
              <div className="relative bg-kinetic-surface-container-high p-4">
                <span className="absolute left-0 top-0 h-3 w-3 border-l-2 border-t-2 border-kinetic-error" />
                <span className="absolute right-0 top-0 h-3 w-3 border-r-2 border-t-2 border-kinetic-error" />
                <span className="absolute bottom-0 left-0 h-3 w-3 border-b-2 border-l-2 border-kinetic-error" />
                <span className="absolute bottom-0 right-0 h-3 w-3 border-b-2 border-r-2 border-kinetic-error" />
                <div className="mb-2 font-mono text-[10px] uppercase tracking-tighter text-kinetic-error">
                  NODE_TELEMETRY
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between font-mono text-[10px]">
                    <span className="text-kinetic-outline">CPU_LOAD</span>
                    <span className="text-kinetic-error">99.8%</span>
                  </div>
                  <div className="h-1 w-full bg-kinetic-surface-container">
                    <div className="h-full w-[99.8%] bg-kinetic-error" />
                  </div>
                  <div className="mt-2 flex justify-between font-mono text-[10px]">
                    <span className="text-kinetic-outline">MEM_USAGE</span>
                    <span className="text-kinetic-error">0xCF440</span>
                  </div>
                  <div className="flex justify-between font-mono text-[10px]">
                    <span className="text-kinetic-outline">UPTIME</span>
                    <span className="text-kinetic-on-surface">00:00:12:04</span>
                  </div>
                </div>
              </div>
              <div className="border-kinetic-error/40 border-l-4 py-2 pl-4">
                <div className="mb-1 font-mono text-[10px] uppercase text-kinetic-outline">
                  SYSTEM_RECOVERY_STATUS
                </div>
                <div className="font-headline text-sm font-semibold text-kinetic-on-surface">
                  ATTEMPTING AUTO-RECOVERY... [FAILED]
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14">
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="text-kinetic-error/60 font-mono text-xs uppercase">
                STACK_TRACE_PREVIEW
              </div>
              <div className="flex gap-2">
                <div className="bg-kinetic-error/30 h-2 w-2" />
                <div className="bg-kinetic-error/30 h-2 w-2" />
                <div className="bg-kinetic-error/30 h-2 w-2" />
              </div>
            </div>
            <pre className="border-kinetic-outline-variant/10 bg-kinetic-surface-container-lowest/80 max-h-64 overflow-x-auto overflow-y-auto whitespace-pre-wrap border p-6 font-mono text-xs leading-relaxed text-kinetic-on-surface-variant backdrop-blur-xl">
              <span className="text-kinetic-error/50">0x00452F:</span>{' '}
              <span className="text-kinetic-on-surface">{message}</span>
              {digestLine ? (
                <>
                  {'\n'}
                  <span className="text-kinetic-error/50">0x004530:</span>{' '}
                  <span className="text-kinetic-on-surface-variant">{digestLine}</span>
                </>
              ) : null}
              {'\n'}
              <span className="text-kinetic-error/50">0x004531:</span>{' '}
              <span className="text-kinetic-on-surface-variant">
                at Next.AppRouter.ErrorBoundary.Render (react-server)
              </span>
              {'\n'}
              <span className="text-kinetic-error/50">0x004532:</span>{' '}
              <span className="font-bold text-kinetic-error">--- END OF STACK TRACE ---</span>
            </pre>
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-10 left-10 hidden items-center gap-4 md:flex">
          <div className="h-1 w-12 bg-kinetic-error" />
          <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-kinetic-error">
            CRITICAL_FAILURE_DETECTED
          </span>
        </div>
      </main>
    </KineticPageShell>
  )
}
